import React, { useCallback, useEffect, useState } from 'react';
import { usePage } from '../../hooks/usePage';
import { nuggetsApi, type NuggetCounts, type NuggetStats } from '../../api/ibra';
import { Card } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { PageHeader } from '../../components/ui/PageHeader';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';

const WINDOWS = [7, 30, 90] as const;

const APP_LABEL: Record<string, string> = {
  consumer: 'Personal app',
  business: 'Business app',
};

/** 0.254 → "25%". A nugget nobody has seen has no rate, not a rate of zero. */
export function percent(rate: number | null): string {
  return rate === null ? 'n/a' : `${Math.round(rate * 100)}%`;
}

/**
 * Whether the feature nuggets earn their place on Home.
 *
 * A nugget is PayIQ choosing what to teach; the how-to list beside it is
 * people asking. Read together: a nugget shown a lot and opened little wants
 * rewording or dropping, and a how-to asked a lot is a screen that does not
 * explain itself, and a good candidate for the next nugget.
 *
 * Counts only, from `GET /admin/ibra/telemetry/nuggets`. Nothing here names a
 * person or a business.
 */
export const IbraNuggets: React.FC = () => {
  usePage('Feature Nuggets');
  const [days, setDays] = useState<(typeof WINDOWS)[number]>(30);
  const [stats, setStats] = useState<NuggetStats | null>(null);
  const [error, setError] = useState(false);

  const load = useCallback(async (window: number) => {
    setError(false);
    setStats(null);
    try {
      setStats(await nuggetsApi.stats(window));
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    void load(days);
  }, [days, load]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <PageHeader
        title="Feature nuggets"
        description="The feature of the day on Home: how often each one is shown, opened and closed, and the how-to questions people ask Ibra most."
        actions={
          <div role="group" aria-label="Window" style={{ display: 'flex', gap: 8 }}>
            {WINDOWS.map((w) => (
              <button
                key={w}
                type="button"
                aria-pressed={days === w}
                onClick={() => setDays(w)}
                style={{
                  padding: '6px 12px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--neutral-300)',
                  background: days === w ? 'var(--neutral-900)' : 'transparent',
                  color: days === w ? 'var(--neutral-100)' : 'var(--neutral-700)',
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                {w} days
              </button>
            ))}
          </div>
        }
      />

      {error ? (
        <EmptyState
          title="Couldn't load the nugget numbers"
          description="Nothing has changed. Try again in a moment."
        />
      ) : !stats ? (
        <Spinner />
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            {stats.audiences.length === 0 ? (
              <Card>
                <div style={{ color: 'var(--neutral-500)', fontSize: 14 }}>
                  No nuggets shown in the last {stats.windowDays} days.
                </div>
              </Card>
            ) : (
              stats.audiences.map((a) => <AppTotals key={a.audience} counts={a} />)
            )}
          </div>

          <Card padding={0}>
            <SectionTitle>By nugget</SectionTitle>
            <Table
              data={stats.nuggets.map((n) => ({ ...n, id: `${n.audience}:${n.guideId}` }))}
              emptyMessage="No nuggets shown in this window."
              columns={[
                { key: 'title', header: 'Nugget', render: (n) => n.title },
                { key: 'app', header: 'App', render: (n) => APP_LABEL[n.audience] ?? n.audience },
                { key: 'shown', header: 'Shown', render: (n) => n.shown.toLocaleString() },
                { key: 'opened', header: 'Opened', render: (n) => n.opened.toLocaleString() },
                { key: 'rate', header: 'Open rate', render: (n) => percent(n.openRate) },
                { key: 'closed', header: 'Closed', render: (n) => n.dismissed.toLocaleString() },
                { key: 'pushed', header: 'Pushed', render: (n) => n.pushed.toLocaleString() },
              ]}
            />
          </Card>

          <Card padding={0}>
            <SectionTitle>
              Most-asked how-tos, personal app
              <span style={{ fontWeight: 400, color: 'var(--neutral-500)', marginLeft: 8 }}>
                Business app: {stats.howTos.businessTotal.toLocaleString()} how-to questions
              </span>
            </SectionTitle>
            <Table
              data={stats.howTos.consumer.map((h) => ({ ...h, id: h.guideId }))}
              emptyMessage="Nobody asked Ibra how to do something in this window."
              columns={[
                { key: 'title', header: 'Guide', render: (h) => h.title },
                { key: 'asked', header: 'Times asked', render: (h) => h.asked.toLocaleString() },
              ]}
            />
          </Card>
        </>
      )}
    </div>
  );
};

function AppTotals({ counts }: { counts: NuggetCounts }) {
  return (
    <Card>
      <div style={{ fontSize: 13, color: 'var(--neutral-500)' }}>
        {APP_LABEL[counts.audience] ?? counts.audience}
      </div>
      <div style={{ fontSize: 28, fontWeight: 600, marginTop: 4 }}>{percent(counts.openRate)}</div>
      <div style={{ fontSize: 13, color: 'var(--neutral-600)', marginTop: 4 }}>
        opened, of {counts.shown.toLocaleString()} shown · {counts.dismissed.toLocaleString()} closed ·{' '}
        {counts.pushed.toLocaleString()} pushed
      </div>
    </Card>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: '16px 24px', fontWeight: 600, borderBottom: '1px solid var(--neutral-300)' }}>
      {children}
    </div>
  );
}

export default IbraNuggets;
